<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal :visible="modal.show"
                   :data="model"
                   :title="computedTitle"
                   @show="onShow"
                   size="lg"
                   @hidden="onHidden"
                   @ok.prevent="handleSubmit(onSubmit)"
                   :show-confirmation="showConfirmation"
                   :ok-title="$t('Submit')"
                   :is-loading="loading"
                   scrollable>
      <div class="row">
        <div class="col-md-6">
          <input-widget :model="model" attribute="is_primary" type="checkbox" :is-switch="true"/>
        </div>
        <div class="col-md-6 text-right">
          <template v-if="model.type === 'billing'">
            <b-button variant="primary" v-if="shippingAddresses.length === 1 && !editObject.uuid"
                      @click="copyAddress(shippingAddresses[0])">
              {{ $t('Same as Shipping') }}
            </b-button>
            <b-dropdown v-else-if="shippingAddresses.length > 1"
                        variant="primary"
                        right id="dropdown-1"
                        :text="$t('Same as Shipping')">
              <b-dropdown-item @click="copyAddress(address)" v-for="address in shippingAddresses"
                               :key="`shipping_${address.uuid}`">
                {{ address.address_1 }}
              </b-dropdown-item>
            </b-dropdown>
          </template>
          <template v-if="model.type === 'shipping'">
            <b-button variant="primary" v-if="billingAddresses.length === 1 && !editObject.uuid"
                      @click="copyAddress(billingAddresses[0])">
              {{ $t('Same as Billing') }}
            </b-button>
            <b-dropdown v-else-if="billingAddresses.length > 1"
                        variant="primary"
                        right id="dropdown-1"
                        :text="$t('Same as Billing')">
              <b-dropdown-item @click="copyAddress(address)" v-for="address in billingAddresses"
                               :key="`billing_${address.uuid}`">
                {{ address.address_1 }}
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </div>
      </div>
      <spinner :show="loadState"/>
      <div class="row" v-if="!loadState">
        <div class="col-md-12">
          <country-select-input-widget attribute="country_code" :model="model"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="address_1"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="address_2"/>
        </div>
        <div class="col-md-6">
          <state-input-widget v-if="isAvalaraEnabled && model.country_code === 'US'" :model="model" attribute="state"/>
          <input-widget :model="model" attribute="state" v-else/>
        </div>
        <div class="col-md-6">
          <county-input-widget
            :model="model" attribute="county" object-uuid-name="county_obj_uuid" :state-uuid="model.state_obj_uuid"
            :enable-state-uuid-watcher="enableCountyUuidWatcherForCityInput">
          </county-input-widget>
        </div>
        <div class="col-md-6">
          <city-input-widget
            :model="model" attribute="city" object-uuid-name="city_obj_uuid" :county-uuid="model.county_obj_uuid"
            :enable-county-uuid-watcher="enableStateUuidWatcherForCountyInput">
          </city-input-widget>
        </div>
        <div class="col-md-6">
          <post-code-input-widget :model="model" attribute="zip_code" :src="CUSTOMER_ADDRESS_FORM"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="email"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="phone"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="first_name"/>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="last_name"/>
        </div>
      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "../../../../core/components/artisio-modal/ArtisioModal";
  import InputWidget from "../../../../core/components/input-widget/InputWidget";
  import {createNamespacedHelpers} from "vuex";
  import AddressForm from "../../models/AddressForm.model";
  import i18n from "../../../../shared/i18n";
  import CountrySelectInputWidget from "../../../../core/widgets/CountrySelectInputWidget";
  import PostCodeInputWidget from "../../../../core/widgets/PostCodeInputWidget";
  import {CUSTOMER_ADDRESS_FORM} from "../../../constants";
  import StateInputWidget from "../../../../core/widgets/StateInputWidget";
  import {GeographicStructureHelper} from "../../../../core/services/geographicStructureHelper";
  import CountyInputWidget from "../../../../core/widgets/CountyInputWidget";
  import CityInputWidget from "../../../../core/widgets/CityInputWidget";
  import Spinner from "../../../../core/components/common/Spinner";

  const {mapState, mapActions} = createNamespacedHelpers('customer/customerView');
  const {mapState: globalMapState, mapGetters: globalMapGetters} = createNamespacedHelpers('global');

  export default {
    name: "AddressFormModal",
    components: {
      Spinner,
      CityInputWidget,
      CountyInputWidget,
      StateInputWidget,
      PostCodeInputWidget, CountrySelectInputWidget, InputWidget, ArtisioModal
    },
    data() {
      return {
        loading: false,
        // This indicates that modal should show confirmation dialog when something is changed inside, and we are trying
        // to close the modal
        showConfirmation: true,
        model: new AddressForm(),
        typeRadioOptions: [
          {text: i18n.t('Billing'), value: 'billing'},
          {text: i18n.t('Shipping'), value: 'shipping'}
        ],
        CUSTOMER_ADDRESS_FORM: CUSTOMER_ADDRESS_FORM,
        loadState: false,
        states: [],
        enableCountyUuidWatcherForCityInput: true,
        enableStateUuidWatcherForCountyInput: true,
      }
    },
    computed: {
      ...globalMapState(['countries', 'currentBranch']),
      ...mapState({
        customer: state => state.customer,
        modal: state => state.addressModal,
        editObject: state => state.addressModal.object,
        shippingAddresses: state => state.addressListData.shippingAddresses,
        billingAddresses: state => state.addressListData.billingAddresses,
      }),
      ...globalMapGetters([
        'isAvalaraEnabled',
      ]),
      computedTitle() {
        if (this.model.type === 'billing') {
          return this.model.uuid ? this.$i18n.t(`Update Billing Address`) : this.$i18n.t(`Add New Billing Address`);
        } else {
          return this.model.uuid ? this.$i18n.t(`Update Shipping Address`) : this.$i18n.t(`Add New Shipping Address`);
        }
      },
    },
    watch: {
      editObject() {
        if (this.editObject && (this.editObject.uuid || this.editObject.type)) {
          let country_code = this.editObject.country_code ? this.editObject.country_code : this.currentBranch.country_code
          this.model = new AddressForm({...this.editObject, country_code});
        }
      }
    },
    methods: {
      ...mapActions(['hideAddressModal', 'createAddress', 'updateAddress']),
      onShow() {
        this.showConfirmation = true;
        this.setDefaultEmailAndPhone()
      },
      onHidden() {
        this.model = new AddressForm();
        this.hideAddressModal();
      },
      async onSubmit() {
        let response;
        this.loading = true;
        this.model.customer_uuid = this.customer.uuid;
        if (!this.model.uuid) {
          response = await this.createAddress(this.model);
        } else {
          response = await this.updateAddress(this.model);
        }
        this.loading = false;

        if (response.success) {
          this.showConfirmation = false;
          this.hideAddressModal();
          if (!this.model.uuid) {
            this.$toast('success', this.$t('Address was successfully created'));
          } else {
            this.$toast('success', this.$t('Address was successfully updated'));
          }
        } else {
          if (response.toastError) {
            this.$errorDialog(response.toastError);
          } else if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else {
            this.model.setMultipleErrors(response.body);
          }
        }
      },
      copyAddress(address) {
        this.enableCountyUuidWatcherForCityInput = false;
        this.enableStateUuidWatcherForCountyInput = false;

        this.model.address_1 = address.address_1;
        this.model.address_2 = address.address_2;
        this.model.state = address.state;
        this.model.state_obj_uuid = address.state_obj_uuid;
        this.model.county = address.county;
        this.model.county_obj_uuid = address.county_obj_uuid;
        this.model.city = address.city;
        this.model.city_obj_uuid = address.city_obj_uuid;
        this.model.zip_code = address.zip_code;
        this.model.country_code = address.country_code;
        this.model.email = address.email;
        this.model.phone = address.phone;
        this.model.first_name = address.first_name;
        this.model.last_name = address.last_name;

        setTimeout(() => {
          this.enableCountyUuidWatcherForCityInput = true;
          this.enableStateUuidWatcherForCountyInput = true;
        }, 500);
      },
      setDefaultEmailAndPhone() {
        if (this.customer) {
          this.model.email = this.model.email ? this.model.email : this.customer.email ? this.customer.email : null
          this.model.phone = this.model.phone ? this.model.phone : this.customer.phone ? this.customer.phone.split(',')[0] : null
        }
      },
      async getStates() {
        this.loadState = true;
        const params = {
          load_all: true,
          country_code: this.model.country_code,
        }
        const {success, body} = await GeographicStructureHelper.getStatesByCountryCode(params);
        if (success) {
          this.states = body;
        }
        this.loadState = false;
      },
    }
  }
</script>

<style scoped>

</style>
